"use client";
import React, { forwardRef, useId } from "react";
import { cx } from "class-variance-authority";
import type { ButtonVariantProps } from "../buttonVariants";
import { buttonVariants } from "../buttonVariants";
import { Spinner } from "../../Loaders";
import { cn } from "../../../utils/cn";
import { sanitizeForTestId } from "../../../utils/testIdUtils";

export interface BaseButtonProps extends ButtonVariantProps {
  className?: string;
}

export interface ButtonProps
  extends BaseButtonProps,
    React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  "data-testid"?: string;
}

/**
 *
 */
function ButtonImpl(
  {
    className,
    vSize,
    vColor,
    vStyle,
    loading,
    disabled,
    onClick,
    children,
    "data-testid": dataTestId,
    ...props
  }: ButtonProps,
  ref: any,
) {
  const css = cn(
    buttonVariants({ className, vColor, vSize, vStyle, loading }),
    "relative",
  );

  // Use React's useId for SSR-safe unique IDs
  const reactId = useId();

  // Auto-generate data-testid from children if not provided
  const generateButtonTestId = (children: React.ReactNode) => {
    // Replace colons in React's useId output to make it a valid test ID
    const safeId = reactId.replace(/:/g, "");
    if (typeof children === "string" && children) {
      const sanitized = sanitizeForTestId(children);
      return `button-${sanitized}-${safeId}`;
    }
    return `button-${safeId}`;
  };

  const testId = dataTestId || generateButtonTestId(children);

  function _onClick(evt: React.MouseEvent<HTMLButtonElement>) {
    if (loading) {
      evt.preventDefault();
      return;
    }
    onClick && onClick(evt);
  }
  const loadingSize = vSize === "lg" ? "1.3rem" : "1.1rem";
  return (
    <button
      className={css}
      ref={ref}
      data-testid={testId}
      {...props}
      disabled={disabled || loading}
      onClick={_onClick}
    >
      {loading ? (
        <Spinner
          className={cx("inset-center absolute")}
          size={loadingSize}
          strokeClassName="stroke-gray-500"
        />
      ) : null}
      <span className={cx(loading && "invisible", "flex items-center gap-2")}>
        {children}
      </span>
    </button>
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(ButtonImpl);
